import { rufe } from './client';

/**
 * Das Suchfenster (M-07) über HTTP.
 *
 * Es gibt hier bewusst KEINE Funktion `suche(begriff: string)`. Gesucht wird
 * über benannte Felder je Art; ein allgemeines Suchfeld ist die eine Sache,
 * die das Pflichtenheft für dieses Fenster ausschließt.
 */

export const ARTEN = [
  'kunde',
  'ansprechpartner',
  'angebot',
  'auftrag',
  'rechnung',
  'termin',
] as const;

export type Suchart = (typeof ARTEN)[number];

/** Die Beschriftung der Artenspalte — Mehrzahl, wie im Pflichtenheft. */
export const ARTENNAME: Readonly<Record<Suchart, string>> = {
  kunde: 'Kunden',
  ansprechpartner: 'Ansprechpartner',
  angebot: 'Angebote',
  auftrag: 'Aufträge',
  rechnung: 'Rechnungen',
  termin: 'Termine',
};

export type Kundenfelder = {
  readonly text?: string;
  readonly ort?: string;
  readonly betreuer?: string;
  readonly status?: string;
};

export type Ansprechpartnerfelder = {
  readonly text?: string;
  readonly kunde?: string;
  readonly funktion?: string;
};

export type Belegfelder = {
  readonly kunde?: string;
  readonly nummer?: string;
  readonly von?: string;
  readonly bis?: string;
  readonly betragVon?: string;
  readonly betragBis?: string;
};

/** SYS-08 (CRM-117): Termine nach Tag, Produkt und Kunde. */
export type Terminfelder = {
  readonly datum?: string;
  readonly produkt?: string;
  readonly kunde?: string;
};

export type Felder = Kundenfelder & Ansprechpartnerfelder & Belegfelder & Terminfelder;

export type Treffer = {
  readonly art: Suchart;
  readonly id: string;
  readonly bezeichnung: string;
  readonly zusatz: string;
};

export type Suchergebnis = {
  readonly art: Suchart;
  readonly felderGesetzt: number;
  readonly zuKurz: readonly string[];
  readonly anzahl: number;
  readonly anzahlGenau: boolean;
  readonly treffer: readonly Treffer[];
  readonly nichtVerfuegbar?: string;
};

export type Verlaufseintrag = {
  readonly art: Suchart;
  readonly datensatz: string;
  readonly bezeichnung: string;
  readonly zusatz: string | null;
  readonly gesehenAm: string;
};

export async function sichtbareArten(): Promise<readonly Suchart[]> {
  const { arten } = await rufe<{ arten: readonly Suchart[] }>('/suche/arten');
  return arten;
}

export async function verlauf(): Promise<readonly Verlaufseintrag[]> {
  const { eintraege } = await rufe<{ eintraege: readonly Verlaufseintrag[] }>('/suche/verlauf');
  return eintraege;
}

/**
 * Einen geöffneten Vorgang vermerken.
 *
 * Es wird NUR die Kennung geschickt. Die Beschriftung holt der Dienst selbst —
 * ein Verlauf, dessen Text aus dem Browser kommt, ist ein Verlauf, in den man
 * schreiben kann, was man will.
 */
export async function vermerken(art: Suchart, datensatz: string): Promise<void> {
  await rufe<void>('/suche/verlauf', { verfahren: 'POST', rumpf: { art, datensatz } });
}

export async function verlaufLoeschen(art?: Suchart): Promise<number> {
  const anhang = art === undefined ? '' : `?art=${encodeURIComponent(art)}`;
  const { geloescht } = await rufe<{ geloescht: number }>(`/suche/verlauf${anhang}`, {
    verfahren: 'DELETE',
  });
  return geloescht;
}

/**
 * Leere Felder werden WEGGELASSEN und nicht als leere Zeichenkette geschickt.
 *
 * Das Schema der API ist `strict` und die Felder sind `optional` — ein
 * mitgeschicktes `ort: ''` wäre ein gesetztes Feld ohne Inhalt, und die
 * Zählung über der Liste zählte es mit.
 */
export async function suchen(
  art: Suchart,
  felder: Felder,
  nurEigene: boolean,
): Promise<Suchergebnis> {
  const gefuellt = Object.fromEntries(
    Object.entries(felder).filter(([, wert]) => typeof wert === 'string' && wert.trim() !== ''),
  );
  return rufe<Suchergebnis>('/suche', {
    verfahren: 'POST',
    rumpf: { art, felder: gefuellt, nurEigene },
  });
}
